import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppShell, Banner } from '@lumal/ui';
import { apiFetch } from '@lumal/lib';
import { API } from './api';
import { btnPrimary, tabsStyle, tabBtn, tabActive } from './styles';
import { has, type MeResponse } from './types';
import { UserBar } from './components/UserBar';
import { SupportPanel } from './features/support/SupportPanel';
import { PlatformPanel } from './features/platform/PlatformPanel';
import { RolesPanel } from './features/platform/RolesPanel';

type Tab = 'support' | 'platform' | 'roles';

export function App() {
  const me = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => apiFetch<MeResponse>(`${API}/portal/me`) });
  const [tab, setTab] = useState<Tab>('support');

  if (me.isLoading) return <AppShell title="Operations Console"><p>Cargando…</p></AppShell>;
  if (!me.data?.authenticated)
    return (
      <AppShell title="Operations Console" subtitle="Plataforma y soporte (interno)">
        <Banner variant="info">No has iniciado sesión.</Banner>
        <a href={`${API}/auth/login`} style={btnPrimary} data-testid="login-button">
          Iniciar sesión (Keycloak operations)
        </a>
      </AppShell>
    );

  const u = me.data.user!;
  const csrf = me.data.csrf_token!;
  const canSupport = has(me.data, 'support:tickets');
  const canPlatform = has(me.data, 'platform:metrics');
  const canRoles = has(me.data, 'platform:roles');
  // Pestaña por defecto según permisos del rol.
  const effectiveTab: Tab = tab === 'support' && !canSupport && canPlatform ? 'platform' : tab;

  return (
    <AppShell title="Operations Console" subtitle="Plataforma Lumal Care — operación interna">
      <UserBar user={u} csrf={csrf} />
      <nav style={tabsStyle} data-testid="ops-nav">
        {canSupport && (
          <button style={{ ...tabBtn, ...(effectiveTab === 'support' ? tabActive : {}) }}
            onClick={() => setTab('support')} data-testid="tab-support">Soporte (CSE)</button>
        )}
        {canPlatform && (
          <button style={{ ...tabBtn, ...(effectiveTab === 'platform' ? tabActive : {}) }}
            onClick={() => setTab('platform')} data-testid="tab-platform">Plataforma</button>
        )}
        {canRoles && (
          <button style={{ ...tabBtn, ...(effectiveTab === 'roles' ? tabActive : {}) }}
            onClick={() => setTab('roles')} data-testid="tab-roles">Roles</button>
        )}
      </nav>
      {!canSupport && !canPlatform && !canRoles && (
        <Banner variant="warn">Tu cuenta no tiene permisos de soporte ni de plataforma.</Banner>
      )}
      {effectiveTab === 'support' && canSupport && <SupportPanel csrf={csrf} />}
      {effectiveTab === 'platform' && canPlatform && <PlatformPanel me={me.data} csrf={csrf} />}
      {effectiveTab === 'roles' && canRoles && <RolesPanel csrf={csrf} />}
    </AppShell>
  );
}
